import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BrainCircuit, Loader2 } from 'lucide-react';
import { trainingApi, type TrainingProgramme } from '../services/api';
import TrainingTabs from '../components/training/TrainingTabs';
import KanbanPage from './KanbanPage';
import ListPage from './ListPage';
import WsjfPage from './WsjfPage';
import GanttPage from './GanttPage';

/**
 * The programme's work, tracked exactly like every other project (EP-26 FE-17 ST-1):
 * the standard Board, Backlog, Priorities (WSJF) and Gantt views, scoped to the
 * programme's training project. Same data, same moves, same checkouts -- no parallel
 * training-only tracker.
 */
const VIEWS: Record<string, (p: { projectId?: string }) => React.ReactElement> = {
  board: KanbanPage, list: ListPage, priorities: WsjfPage, gantt: GanttPage,
};

export default function TrainingWorkPage() {
  const { programId = '', view = 'board' } = useParams<{ programId: string; view: string }>();
  const [programme, setProgramme] = useState<TrainingProgramme | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setProgramme(null);
    trainingApi.programme(programId).then(setProgramme).catch((e) => setError(String(e?.message || e)));
  }, [programId]);

  const View = VIEWS[view];
  const projectId = programme?.trainingProjectId;
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-6 pt-6 space-y-3 shrink-0">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <BrainCircuit className="w-5 h-5" /> Model Training{programme ? ` · ${programme.name}` : ''}
        </h1>
        <TrainingTabs programId={programId} />
      </div>
      {error && <p className="px-6 text-sm text-red-600">{error}</p>}
      {!programme && !error && <p className="px-6 py-4 text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
      {programme && !projectId && <p className="px-6 py-4 text-sm text-amber-700">This programme has no training project yet. Run its setup first.</p>}
      {programme && projectId && View && <div className="flex-1 min-h-0 overflow-auto"><View projectId={projectId} /></div>}
      {!View && <p className="px-6 py-4 text-sm text-gray-600">No view named {view}.</p>}
    </div>
  );
}
